// A separator's label, authored as text with field tokens mixed in:
//
//   "{occurrenceStart|ddd, MMM D, YYYY} - sold out"
//
// and emitted as an array of the engine's own source-defs:
//
//   [ { source: "params", paramName: "after", path: "occurrenceStart",
//       transform: { type: "dateFormat", params: { pattern: "ddd, MMM D, YYYY" } } },
//     { source: "constant", value: " - sold out" } ]
//
// No new engine primitive: `params` / `constant` are what visibilityExpression
// already uses, and dateFormat ships in both runtimes. The feed resolves the
// parts and joins them, so one rule can label every separator it produces from
// the boundary row's own data.

export const LABEL_SCOPE = "after";

const PATTERN_TOKEN = /\{([^}|]+)(?:\|([^}]*))?\}/g;

// The authored-pattern list, minus the two entries in options/date-format.js
// that are not dayjs patterns: `global` defers to a publication setting this
// label has no access to, and `timeAgo` is a renderer, not a format string.
// Kept as data rather than an import so this module stays free of the option
// class (and dayjs) and can be unit-tested on its own.
const PATTERN_EXCLUDED_FORMAT_VALUES = ["global", "timeAgo"];

// Formats a separator wants that a date ELEMENT never offers: a separator that
// groups by month is named by the month, not by a full date.
const SEPARATOR_DATE_FORMATS = [
  { value: "MMMM YYYY", label: "Month + year" },
  { value: "MMMM", label: "Month" },
  { value: "dddd", label: "Weekday" },
  { value: "YYYY", label: "Year" },
];

const PATTERN_DAY_LABEL = "dddd, MMMM D, YYYY";
const PATTERN_MONTH_LABEL = "MMMM YYYY";

export function buildLabelDateFormatOptions(dateFormatOptions = []) {
  const excluded = new Set(PATTERN_EXCLUDED_FORMAT_VALUES);
  const shipped = (dateFormatOptions ?? []).filter(option => !excluded.has(option.value));
  const known = new Set(shipped.map(option => option.value));
  return [...shipped, ...SEPARATOR_DATE_FORMATS.filter(option => !known.has(option.value))];
}

// The separator a SPLIT condition implies. Splitting by day names each group by
// its day; by month, by its month; a plain change, by the value itself. Returns
// "" for anything that is not a split, so a match rule keeps the typed name.
export function buildDefaultSplitLabel(fieldName, operator) {
  if (!fieldName) {
    return "";
  }
  if (operator === "dayChanged") {
    return `{${fieldName}|${PATTERN_DAY_LABEL}}`;
  }
  if (operator === "monthChanged") {
    return `{${fieldName}|${PATTERN_MONTH_LABEL}}`;
  }
  if (operator === "changed") {
    return `{${fieldName}}`;
  }
  return "";
}

// Parse authored text into parts. Unknown field names are left to the caller to
// validate: parsing must not silently drop a token the author can see.
function parseLabelTemplate(text) {
  const source = String(text ?? "");
  const parts = [];
  let cursor = 0;
  PATTERN_TOKEN.lastIndex = 0;
  let match = PATTERN_TOKEN.exec(source);
  while (match !== null) {
    if (match.index > cursor) {
      parts.push({ kind: "text", value: source.slice(cursor, match.index) });
    }
    parts.push({
      kind: "field",
      fieldName: match[1].trim(),
      format: (match[2] ?? "").trim(),
    });
    cursor = match.index + match[0].length;
    match = PATTERN_TOKEN.exec(source);
  }
  if (cursor < source.length) {
    parts.push({ kind: "text", value: source.slice(cursor) });
  }
  return parts;
}

// The editor's view of a label: iTemplateInput's segment shape, so the author
// sees "Events on [Start date · Wed, Oct 7]" and never the braces. Round-trips
// through fromSegments byte-for-byte for any label the editor can produce.
export function toSegments(text) {
  return parseLabelTemplate(text).map(part => {
    if (part.kind === "text") {
      return { kind: "text", value: part.value };
    }
    const segment = { kind: "field", field: part.fieldName };
    if (part.format) {
      segment.format = part.format;
    }
    return segment;
  });
}

export function fromSegments(segments) {
  return (segments ?? [])
      .filter(Boolean)
      .map(segment => {
        if (segment.kind === "text") {
          return String(segment.value ?? "");
        }
        const format = String(segment.format ?? "").trim();
        return format ? `{${segment.field}|${format}}` : `{${segment.field}}`;
      })
      .join("");
}

// The label as a one-line description with each token replaced by the field's
// display name, for places that show the separator but cannot resolve it.
export function summariseLabel(text, fieldCandidates = []) {
  const labels = new Map((fieldCandidates ?? []).map(candidate => [candidate.fieldName, candidate.label ?? candidate.fieldName]));
  return parseLabelTemplate(text)
      .map(part => {
        if (part.kind === "text") {
          return part.value;
        }
        return labels.get(part.fieldName) ?? part.fieldName;
      })
      .join("");
}

// The label with each token shown as a sample, for the builder canvas, which has
// no row to resolve it from. `formatSample(pattern)` formats a date the way the
// label editor's chip examples do; a format-less token shows its field's name.
export function previewLabel(text, formatSample) {
  return parseLabelTemplate(text)
      .map(part => {
        if (part.kind === "text") {
          return part.value;
        }
        if (part.format) {
          return formatSample(part.format);
        }
        return part.fieldName
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/^./, first => first.toUpperCase());
      })
      .join("");
}

// Authored parts -> the source-defs the feed resolves. Text-only labels emit a
// single constant, which is byte-identical to what the old flat label produced.
export function toLabelParts(text) {
  return parseLabelTemplate(text)
      .filter(part => part.kind === "field" || part.value !== "")
      .map(part => {
        if (part.kind === "text") {
          return { source: "constant", value: part.value };
        }
        const def = {
          source: "params",
          paramName: LABEL_SCOPE,
          path: part.fieldName,
        };
        if (part.format) {
          def.transform = { type: "dateFormat", params: { pattern: part.format } };
        }
        return def;
      });
}

// True when the label needs the boundary row -- i.e. it carries a field token.
// A constant-only label is the same on every separator the rule produces, which is
// right for a MATCH rule and wrong for a SPLIT one; collectRuleNotes warns on that.
export function doesLabelReadFields(text) {
  return parseLabelTemplate(text).some(part => part.kind === "field");
}

function collectLabelFieldNames(text) {
  return parseLabelTemplate(text)
      .filter(part => part.kind === "field")
      .map(part => part.fieldName);
}

// Tokens naming a field the row entity does not declare resolve to nothing at
// render, so the separator would silently lose a piece of itself.
export function collectLabelProblems(text, fieldCandidates = []) {
  const known = new Set((fieldCandidates ?? []).map(candidate => candidate.fieldName));
  const problems = [];
  collectLabelFieldNames(text).forEach(fieldName => {
    if (!fieldName) {
      problems.push("A field token in the separator has no field name.");
      return;
    }
    if (known.size > 0 && !known.has(fieldName)) {
      problems.push(`The separator uses "${fieldName}", which is not a field on these items.`);
    }
  });
  return problems;
}
